import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Route,
    Routes,
    useLocation,
    useMatch,
    useSearchParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
// TODO: Update TypeScript config to allow for arbitrary CSS imports
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import 'react-toastify/dist/ReactToastify.css';

import amplitude from 'libs/amplitude';

import { useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';
import { useNavigateWithParams } from 'hooks/useNavigateWithParams';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import { SignIn } from 'components/layout/SignIn';
import { SignUp } from 'components/layout/SignUp';
import { PursuitSignUp } from './PursuitSignUp';
import { PasswordReset } from 'components/layout/PasswordReset';
import { Confirm } from 'components/layout/Confirm';
import { CouncilMember } from 'components/layout/CouncilMember';
import { OnboardingContainer } from 'components/layout/OnboardingContainer';
import { PursuitApplication } from 'components/layout/PursuitApplication';
import { PursuitContainer } from 'components/layout/PursuitContainer';
import { Directory } from 'components/layout/Directory';
import { PursuitResources } from 'components/layout/PursuitResources';
import { SessionDetails } from 'components/layout/SessionDetails';
import { SessionList } from 'components/layout/SessionList';
import { Insights } from 'components/layout/Insights';
import { EmailConfirmed } from 'components/layout/EmailConfirmed';
import { GroupReports } from 'legacy/components/GroupReports';
import { Coach } from 'components/layout/Coach';
import { GroupList } from 'components/layout/GroupList';
import { PendingRecaps } from 'legacy/components/PendingRecaps';
import { RecapHistory } from 'legacy/components/RecapHistory';
import { CoachResources } from 'components/ui/CoachResources';
import { Footer } from 'components/ui/Footer';
import { Modal } from 'components/layout/Modal';
import { PursuitProfileRawData } from 'components/layout/ProfileContainer';

import { colors, GlobalStyles, transparencies } from 'styles/theme';
import { AppWrapper, FooterWrapper } from 'styles/appRoutes';
import { usePrevious } from 'legacy/hooks/usePrevious';

export const AppRoutes = () => {
    const location = useLocation();
    const { hasInitialized, isSignedIn, userProfile } = useAuth();
    const { showWarningToast } = useToast();
    const { isMobileScreenSize } = useResponsiveScreenSizes();
    const navigateWithParams = useNavigateWithParams();

    const passwordResetRoute = useMatch('/password-reset/*');
    const confirmRoute = useMatch('/confirm/*');
    const emailConfirmedRoute = useMatch('/email-confirmed/*');
    const signInRoute = useMatch('/sign-in/*');
    const signUpRoute = useMatch('/sign-up/*');
    const applyRoute = useMatch('/apply/*');
    const onboardingRoute = useMatch('/onboarding/*');
    const pursuitApplicationRoute = useMatch('/pursuit-application/*');
    const pursuitRoute = useMatch('/pursuit/*');
    const sponsorRoute = useMatch('/sponsor/*');
    const coachRoute = useMatch('/coach/*');
    const memberRoute = useMatch('/member/*');

    const [searchParams, setSearchParams] = useSearchParams();

    const [hasDisplayedScreenSizeToast, setHasDisplayedScreenSizeToast] =
        useState<boolean>(false);

    const scrollRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const scrollToTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0 });
    }, []);

    const rawData = userProfile?.raw_data as PursuitProfileRawData | null;

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId != null) {
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set('webflow_session_id', sessionId);
            amplitude.identify(identifyEvent);

            const params = new URLSearchParams(searchParams);
            params.delete('session_id');
            setSearchParams(params, { replace: true });
        }
        // We only want to run this effect once when the component mounts
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            !hasDisplayedScreenSizeToast &&
            window.innerWidth < 1120 &&
            coachRoute != null
        ) {
            setHasDisplayedScreenSizeToast(true);
            showWarningToast(
                'Mobile devices and smaller browser window sizes are not yet fully supported'
            );
        }
    }, [coachRoute, hasDisplayedScreenSizeToast, showWarningToast]);

    useEffect(() => {
        if (!hasInitialized) {
            return;
        }

        if (
            passwordResetRoute == null &&
            confirmRoute == null &&
            emailConfirmedRoute == null
        ) {
            if (
                signInRoute == null &&
                signUpRoute == null &&
                applyRoute == null &&
                !isSignedIn
            ) {
                navigateWithParams('/sign-in');
            } else if (userProfile != null) {
                if (
                    sponsorRoute != null &&
                    userProfile?.sponsor_id != null &&
                    userProfile.sponsor_id !== ''
                ) {
                    return;
                } else if (
                    sponsorRoute == null &&
                    userProfile?.sponsor_id != null &&
                    userProfile.sponsor_id !== ''
                ) {
                    navigateWithParams('/sponsor');
                } else if (
                    coachRoute != null &&
                    userProfile?.is_coach === true
                ) {
                    return;
                } else if (
                    coachRoute == null &&
                    userProfile?.is_coach === true
                ) {
                    navigateWithParams('/coach');
                } else if (
                    pursuitRoute != null &&
                    (userProfile?.type === 'Pursuit Retreat' ||
                        userProfile?.type === 'Pursuit Digital')
                ) {
                    return;
                } else if (
                    pursuitRoute == null &&
                    (userProfile?.type === 'Pursuit Retreat' ||
                        userProfile?.type === 'Pursuit Digital')
                ) {
                    navigateWithParams('/pursuit');
                    return;
                } else if (
                    pursuitApplicationRoute != null &&
                    userProfile?.temp_group_sponsor_id == null
                ) {
                    return;
                } else if (
                    memberRoute != null &&
                    (userProfile?.member_groups?.length ?? 0) > 0
                ) {
                    return;
                } else if (
                    memberRoute == null &&
                    (userProfile?.member_groups?.length ?? 0) > 0
                ) {
                    navigateWithParams('/member');
                } else if (
                    pursuitApplicationRoute == null &&
                    userProfile?.temp_group_sponsor_id == null
                ) {
                    navigateWithParams('/pursuit-application');
                    return;
                } else if (
                    onboardingRoute != null &&
                    (userProfile?.member_groups?.length ?? 0) === 0
                ) {
                    return;
                } else if (
                    onboardingRoute == null &&
                    (userProfile?.member_groups?.length ?? 0) === 0
                ) {
                    navigateWithParams('/onboarding');
                }
            }
        }
    }, [
        applyRoute,
        coachRoute,
        confirmRoute,
        emailConfirmedRoute,
        hasInitialized,
        isSignedIn,
        memberRoute,
        navigateWithParams,
        onboardingRoute,
        passwordResetRoute,
        pursuitApplicationRoute,
        pursuitRoute,
        signInRoute,
        signUpRoute,
        sponsorRoute,
        userProfile,
    ]);

    const previousLocation = usePrevious(location);
    useEffect(() => {
        if (location !== previousLocation) {
            scrollToTop();
        }
    }, [location, previousLocation, scrollToTop]);

    const appRoutesAndOverlays = useMemo(
        () =>
            hasInitialized ? (
                <>
                    <Routes>
                        {/* Routes accessible regardless of authentication */}
                        <Route
                            path="password-reset"
                            element={<PasswordReset />}
                        />
                        <Route
                            path="confirm"
                            element={<Confirm appScrollToTop={scrollToTop} />}
                        />
                        <Route
                            path="email-confirmed"
                            element={<EmailConfirmed />}
                        />

                        {/* Routes accessible only when unauthenticated */}
                        <Route
                            path="sign-in"
                            element={<SignIn appScrollToTop={scrollToTop} />}
                        />
                        <Route
                            path="sign-up/pursuit"
                            element={<PursuitSignUp />}
                        />
                        <Route
                            path="sign-up/:tempGroupSponsorId?/:sponsorName?"
                            element={<SignUp />}
                        />
                        <Route
                            path="apply/:tempGroupSponsorId?/:sponsorName?"
                            element={<SignUp />}
                        />

                        {/* Routes accessible only to authenticated sponsors */}
                        <Route path="sponsor" element={<GroupReports />} />

                        {/* Routes accessible only to authenticated coaches */}
                        <Route path="coach" element={<Coach />}>
                            <Route
                                path="submit-recaps/*"
                                element={<PendingRecaps />}
                            />
                            <Route
                                path="past-recaps/*"
                                element={<RecapHistory />}
                            />
                            <Route path="groups/*" element={<GroupList />} />
                            <Route
                                path="resources/*"
                                element={<CoachResources />}
                            />
                            <Route path="directory/*" element={<Directory />} />
                        </Route>

                        {/* Routes accessible only to authenticated users with no app access */}
                        <Route
                            path="onboarding"
                            element={<OnboardingContainer />}
                        />
                        <Route
                            path="pursuit-application"
                            element={<PursuitApplication />}
                        />

                        {/* Routes accessible only to authenticated council members */}
                        <Route path="member" element={<CouncilMember />}>
                            <Route
                                path="scheduled-session/*"
                                element={<SessionDetails />}
                            />
                            <Route
                                path="sessions/*"
                                element={<SessionList />}
                            />
                            <Route
                                path="sessions/:sessionNumber/*"
                                element={<SessionDetails />}
                            />
                            <Route path="insights/*" element={<Insights />} />
                        </Route>

                        {/* Routes accessible only to authenticated Pursuit members */}
                        <Route
                            path="pursuit"
                            element={
                                <PursuitContainer
                                    appScrollToTop={scrollToTop}
                                />
                            }
                        >
                            <Route
                                path="sessions/*"
                                element={<SessionList isPursuit />}
                            />
                            <Route
                                path="sessions/:sessionNumber/*"
                                element={<SessionDetails isPursuit />}
                            />
                            <Route
                                path="insights/*"
                                element={<Insights isPursuit />}
                            />
                            <Route path="directory/*" element={<Directory />} />
                            <Route
                                path="resources/*"
                                element={<PursuitResources />}
                            />
                        </Route>
                    </Routes>

                    <FooterWrapper>
                        <Footer
                            hasSignOut={
                                onboardingRoute != null ||
                                sponsorRoute != null ||
                                pursuitApplicationRoute != null ||
                                (isSignedIn &&
                                    (passwordResetRoute != null ||
                                        confirmRoute != null)) ||
                                (pursuitRoute != null &&
                                    !rawData?.has_completed_pursuit_onboarding) ||
                                ((memberRoute != null || coachRoute != null) &&
                                    isMobileScreenSize)
                            }
                            $textColor={
                                signInRoute != null ||
                                signUpRoute != null ||
                                applyRoute != null ||
                                passwordResetRoute != null ||
                                confirmRoute != null ||
                                pursuitApplicationRoute != null
                                    ? colors.grey30
                                    : transparencies.white80
                            }
                        />
                    </FooterWrapper>

                    {memberRoute != null ||
                    sponsorRoute != null ||
                    coachRoute != null ||
                    pursuitRoute != null ? (
                        <Modal />
                    ) : null}
                </>
            ) : null,
        [
            applyRoute,
            coachRoute,
            confirmRoute,
            hasInitialized,
            isMobileScreenSize,
            isSignedIn,
            memberRoute,
            onboardingRoute,
            passwordResetRoute,
            pursuitApplicationRoute,
            pursuitRoute,
            rawData?.has_completed_pursuit_onboarding,
            scrollToTop,
            signInRoute,
            signUpRoute,
            sponsorRoute,
        ]
    );

    return (
        <>
            <GlobalStyles />
            <AppWrapper
                ref={scrollRef}
                $isCloudBackground={
                    signInRoute != null ||
                    signUpRoute != null ||
                    applyRoute != null ||
                    passwordResetRoute != null ||
                    confirmRoute != null ||
                    pursuitApplicationRoute != null
                }
            >
                <ToastContainer />
                {(pursuitRoute == null ||
                    !rawData?.has_completed_pursuit_onboarding) &&
                memberRoute == null ? (
                    appRoutesAndOverlays
                ) : (
                    <div>{appRoutesAndOverlays}</div>
                )}
            </AppWrapper>
        </>
    );
};
